import { HttpTypes } from '@/types';
import { ICategory } from '@/types/category';
import {
  AdminProductCategoryListResponse,
  AdminProductCreateResponse,
  AdminProductDeleteResponse,
} from '@/types/http';
import { BaseProduct } from '@/types/http/product/common';
import { IProduct, IVariantType, ProductItemExtended } from '@/types/product';
import { AxiosInstance } from 'axios';

class Products {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async list(queryParams: unknown): Promise<HttpTypes.IResponse<BaseProduct[]>> {
    const result = await this.client.get('/product/unit', {
      params: queryParams,
    });

    return result.data;
  }

  async detail(id: string): Promise<HttpTypes.IResponse<BaseProduct>> {
    const result = await this.client.get(`/product/unit/${id}`);
    return result.data;
  }

  async create(
    data: HttpTypes.AdminProductCreatePayload
  ): Promise<HttpTypes.IResponse<AdminProductCreateResponse>> {
    const result = await this.client.post('/product/unit', data);
    return result.data;
  }

  async update(data: BaseProduct): Promise<HttpTypes.IResponse<BaseProduct>> {
    const result = await this.client.put(`/product/unit/${data.id}`, data);
    return result.data;
  }

  async delete(id: string): Promise<HttpTypes.IResponse<AdminProductDeleteResponse>> {
    const result = await this.client.delete(`/product/unit/${id}`);
    return result.data;
  }

  async deleteUnitItems(data: {
    id: string;
    item_ids: string[];
  }): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.delete(`/product/unit/${data.id}/items`, {
      data,
    });
    return result.data;
  }

  async deleteGroup(data: { ids: string[] }): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.delete('/product/unit/groups', {
      data,
    });
    return result.data;
  }

  async updateStatusMultiple(data: {
    ids: string[];
    is_active: boolean;
  }): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.post('/product/unit/status', data);
    return result.data;
  }

  async listItem(
    id: string,
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<BaseProduct>> {
    const result = await this.client.get(`/product/unit/${id}/product-item`, {
      params: query,
    });
    return result.data;
  }

  async listParentCategoryByChildId(id: string): Promise<HttpTypes.IResponse<ICategory[]>> {
    const result = await this.client.get(`/product/category/child/${id}`);
    return result.data;
  }

  async listChildCategoryByParentId(parentId: string): Promise<AdminProductCategoryListResponse> {
    const result = await this.client.get(`/product/category/parent/${parentId}`);
    return result.data;
  }

  async listAllParentCategory(
    query?: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<ICategory[]>> {
    const result = await this.client.get('/product/category/parent', {
      params: query,
    });
    return result.data;
  }

  async listAllCategory(query: HttpTypes.FindParams): Promise<HttpTypes.IResponse<ICategory[]>> {
    const result = await this.client.get('/product/category', {
      params: query,
    });
    return result.data;
  }

  async listCategoryTree(id: string): Promise<HttpTypes.AdminProductCategoryTreeListResponse> {
    const result = await this.client.get(`/product/category/tree/${id}`);
    return result.data;
  }

  async listVariantType(): Promise<HttpTypes.IResponse<IVariantType[]>> {
    const result = await this.client.get('/product/variant-type');
    return result.data;
  }

  async retrieveProductItemDetails(
    id: string,
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<ProductItemExtended>> {
    const result = await this.client.get(`/product/unit/items/${id}`, {
      params: query,
    });
    return result.data;
  }

  async addProductCollection(data: {
    product_ids: string[];
    collection_id: string;
  }): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.post('/product/unit/collection/add', data);
    return result.data;
  }

  async exportProduct(
    data: HttpTypes.AdminProductExportPayload
  ): Promise<HttpTypes.AdminProductExportResponse> {
    const result = await this.client.post('/product/unit/export', data);
    return result.data;
  }

  async listLog(
    productId: string,
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminProductLogListResponse> {
    const result = await this.client.get(`/product/unit/history/${productId}`, {
      params: query,
    });
    return result.data;
  }

  async addProductToMultipleCollections(data: {
    product_ids: string[];
    collection_ids: string[];
  }): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.post('/product/unit/collections/add', data);
    return result.data;
  }

  async importProduct(data: FormData): Promise<HttpTypes.IResponse<IProduct>> {
    const result = await this.client.post('/product/unit/import', data);
    return result.data;
  }

  async importProductHistory(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminProductEximResponse> {
    const result = await this.client.get('/product/unit/jobs', {
      params: query,
    });

    return result.data;
  }

  async importProductHistoryDetail(
    id: string,
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminProductEximDetailResponse> {
    const result = await this.client.get(`/product/unit/jobs/${id}`, {
      params: query,
    });

    return result.data;
  }

  async listItemsAll(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminProductItemsListInfiniteResponse> {
    const result = await this.client.get(`/product/unit/product-items`, {
      params: query,
    });
    return result.data;
  }

  async listReviewSummary(
    productId: string,
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.ProductReviewSummaryInfiniteResponse> {
    const result = await this.client.get(`/product/unit/${productId}/reviews/summary`, {
      params: query,
    });
    return result.data;
  }
}

export default Products;
